import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, ExternalLink, Mail, Loader2, Send, Users, Search } from "lucide-react";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { fetchCategories } from "@/lib/api";
import { fetchMe, setToken, adminFetchArticles, adminDeleteArticle, adminFetchMessages, adminFetchSubscribers, adminSendNewsletter, adminFetchNewsletterDraft, formatApiError } from "@/lib/auth";
import { AdminMonetization } from "@/pages/admin/AdminMonetization";
import { AdminCampaigns } from "@/pages/admin/AdminCampaigns";
import { AdminCategories } from "@/pages/admin/AdminCategories";
import { AdminComments } from "@/pages/admin/AdminComments";

export default function AdminDashboard() {
    const [user, setUser] = useState(null);
    const [articles, setArticles] = useState([]);
    const [messages, setMessages] = useState([]);
    const [subscribers, setSubscribers] = useState([]);
    const [newsForm, setNewsForm] = useState({ subject: "", message: "", article_url: null });
    const [draftSource, setDraftSource] = useState(null);
    const [sending, setSending] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(false);
    const [search, setSearch] = useState("");
    const [filterCat, setFilterCat] = useState("Todas");
    const { data: dashCategories = [] } = useQuery({ queryKey: ["categories"], queryFn: fetchCategories });

    const filteredArticles = useMemo(() => {
        const q = search.trim().toLowerCase();
        return articles.filter((a) => {
            const matchQ = !q || a.title.toLowerCase().includes(q) || a.excerpt?.toLowerCase().includes(q);
            const matchCat = filterCat === "Todas" || a.category === filterCat;
            return matchQ && matchCat;
        });
    }, [articles, search, filterCat]);
    const [deleting, setDeleting] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchMe()
            .then(setUser)
            .catch(() => {
                setToken(null);
                navigate("/admin/login", { replace: true });
            });
        adminFetchArticles().then(setArticles).catch(() => {});
        adminFetchMessages().then(setMessages).catch(() => {});
        adminFetchSubscribers().then(setSubscribers).catch(() => {});
    }, [navigate]);

    const sendNewsletter = async (e) => {
        e.preventDefault();
        if (!window.confirm(`Enviar esta edição para ${subscribers.length} inscrito(s)?`)) return;
        setSending(true);
        try {
            const res = await adminSendNewsletter(newsForm);
            toast.success(`Newsletter enviada para ${res.sent} de ${res.recipients} inscritos.`);
            setNewsForm({ subject: "", message: "", article_url: null });
            setDraftSource(null);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSending(false);
        }
    };

    const buildFromLatest = async () => {
        setLoadingDraft(true);
        try {
            const draft = await adminFetchNewsletterDraft();
            setNewsForm({ subject: draft.subject, message: draft.message, article_url: draft.article_url });
            setDraftSource(draft.article_title);
            toast.success("Edição montada a partir do artigo mais recente. Revise antes de enviar.");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setLoadingDraft(false);
        }
    };

    const logout = () => {
        setToken(null);
        navigate("/admin/login", { replace: true });
    };

    const remove = async (article) => {
        if (!window.confirm(`Excluir "${article.title}"? Esta ação não pode ser desfeita.`)) return;
        setDeleting(article.id);
        try {
            await adminDeleteArticle(article.id);
            setArticles(articles.filter((a) => a.id !== article.id));
            toast.success("Artigo excluído.");
        } catch {
            toast.error("Não foi possível excluir o artigo.");
        } finally {
            setDeleting(null);
        }
    };

    if (!user) {
        return (
            <main className="min-h-screen flex items-center justify-center">
                <Loader2 size={24} className="animate-spin text-gold" />
            </main>
        );
    }

    return (
        <main data-testid="admin-dashboard" className="pt-28 md:pt-32 pb-20 max-w-6xl mx-auto px-4 md:px-8">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
                <div>
                    <span className="text-xs uppercase tracking-[0.3em] text-gold font-medium">Redação</span>
                    <h1 className="font-serif text-3xl md:text-4xl tracking-tight mt-2">Painel de artigos</h1>
                    <p className="text-xs text-slate-500 mt-2">Conectado como {user.email}</p>
                </div>
                <div className="flex gap-3">
                    <Link
                        to="/admin/artigo/novo"
                        data-testid="admin-new-article-btn"
                        className="flex items-center gap-2 bg-gold text-ink font-medium px-5 py-2.5 text-sm hover:bg-golddark transition-colors"
                    >
                        <Plus size={15} /> Novo artigo
                    </Link>
                    <button
                        data-testid="admin-logout-btn"
                        onClick={logout}
                        className="flex items-center gap-2 border border-white/15 text-slate-300 px-5 py-2.5 text-sm hover:border-red-400 hover:text-red-400 transition-colors"
                    >
                        <LogOut size={15} /> Sair
                    </button>
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-3 mb-5">
                <div className="relative flex-1">
                    <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input
                        data-testid="admin-search-input"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar por título ou resumo..."
                        className="w-full bg-paper border border-white/10 focus:border-gold/60 outline-none pl-11 pr-4 py-3 text-sm placeholder:text-slate-600 transition-colors"
                    />
                </div>
                <select
                    data-testid="admin-category-filter"
                    value={filterCat}
                    onChange={(e) => setFilterCat(e.target.value)}
                    className="bg-paper border border-white/10 focus:border-gold/60 outline-none px-4 py-3 text-sm transition-colors"
                >
                    <option value="Todas">Todas as categorias</option>
                    {dashCategories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
                {(search || filterCat !== "Todas") && (
                    <span data-testid="admin-filter-count" className="self-center text-xs text-slate-500">
                        {filteredArticles.length} de {articles.length} artigos
                    </span>
                )}
            </div>

            <div className="border border-white/10">
                <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 border-b border-white/10 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    <span className="col-span-6">Título</span>
                    <span className="col-span-2">Categoria</span>
                    <span className="col-span-2">Publicado em</span>
                    <span className="col-span-2 text-right">Ações</span>
                </div>
                {filteredArticles.map((a) => (
                    <div key={a.id} data-testid={`admin-article-row-${a.slug}`} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-5 py-4 border-b border-white/5 items-center hover:bg-white/[0.02] transition-colors">
                        <div className="col-span-6">
                            <p className="font-serif text-base leading-snug">{a.title}</p>
                            <p className="text-[11px] text-slate-600 mt-1">/artigo/{a.slug} {a.featured && <span className="text-gold ml-2">★ destaque</span>}{a.status === "draft" && <span className="text-amber-500 ml-2">● rascunho</span>}</p>
                        </div>
                        <span className="col-span-2 text-xs text-slate-400">{a.category}</span>
                        <span className="col-span-2 text-xs text-slate-500">{new Date(a.published_at + "T12:00:00").toLocaleDateString("pt-BR")}</span>
                        <div className="col-span-2 flex justify-end gap-2">
                            <Link to={`/artigo/${a.slug}`} target="_blank" data-testid={`admin-view-${a.slug}`} className="p-2 border border-white/10 text-slate-400 hover:text-gold hover:border-gold/50 transition-colors" title="Ver">
                                <ExternalLink size={14} />
                            </Link>
                            <Link to={`/admin/artigo/${a.id}/editar`} data-testid={`admin-edit-${a.slug}`} className="p-2 border border-white/10 text-slate-400 hover:text-gold hover:border-gold/50 transition-colors" title="Editar">
                                <Pencil size={14} />
                            </Link>
                            <button onClick={() => remove(a)} data-testid={`admin-delete-${a.slug}`} disabled={deleting === a.id} className="p-2 border border-white/10 text-slate-400 hover:text-red-400 hover:border-red-400/50 transition-colors disabled:opacity-50" title="Excluir">
                                <Trash2 size={14} />
                            </button>
                        </div>
                    </div>
                ))}
                {filteredArticles.length === 0 && (
                    <p className="px-5 py-10 text-center text-sm text-slate-500">
                        {articles.length === 0 ? "Nenhum artigo ainda." : "Nenhum artigo encontrado com esses filtros."}
                    </p>
                )}
            </div>

            <div className="mt-14">
                <h2 className="font-serif text-2xl tracking-tight mb-6 flex items-center gap-3">
                    <Send size={18} className="text-gold" /> Enviar newsletter
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="border border-white/10 bg-paper p-6">
                        <div className="flex items-center gap-3 mb-2">
                            <Users size={18} className="text-gold" />
                            <span className="font-serif text-4xl" data-testid="subscribers-count">{subscribers.length}</span>
                        </div>
                        <p className="text-xs text-slate-500 uppercase tracking-[0.2em]">inscritos na lista</p>
                        <div className="mt-5 max-h-40 overflow-y-auto flex flex-col gap-1.5">
                            {subscribers.map((s) => (
                                <span key={s.email} className="text-xs text-slate-500 truncate">{s.email}</span>
                            ))}
                        </div>
                    </div>
                    <form data-testid="newsletter-send-form" onSubmit={sendNewsletter} className="lg:col-span-2 border border-white/10 bg-paper p-6 flex flex-col gap-4">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Nova edição</span>
                            <button
                                type="button"
                                data-testid="newsletter-draft-latest-btn"
                                onClick={buildFromLatest}
                                disabled={loadingDraft}
                                className="flex items-center gap-2 border border-gold/40 text-gold px-4 py-2 text-xs uppercase tracking-[0.15em] hover:bg-gold hover:text-ink transition-colors disabled:opacity-50"
                            >
                                {loadingDraft && <Loader2 size={13} className="animate-spin" />}
                                Montar da última publicação
                            </button>
                        </div>
                        {draftSource && (
                            <p data-testid="newsletter-draft-source" className="text-xs text-slate-500 border border-white/10 bg-ink px-4 py-2.5">
                                Baseada no artigo: <span className="text-gold">{draftSource}</span> — revise o texto antes de enviar.
                            </p>
                        )}
                        <div>
                            <label className="block text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Assunto da edição</label>
                            <input
                                data-testid="newsletter-subject-input"
                                required
                                minLength={5}
                                value={newsForm.subject}
                                onChange={(e) => setNewsForm({ ...newsForm, subject: e.target.value })}
                                placeholder="Ex.: Por que a Selic importa para o seu bolso"
                                className="w-full bg-ink border border-white/10 focus:border-gold/60 outline-none px-4 py-3 text-sm placeholder:text-slate-600 transition-colors"
                            />
                        </div>
                        <div>
                            <label className="block text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Mensagem (cada linha vira um parágrafo)</label>
                            <textarea
                                data-testid="newsletter-message-input"
                                required
                                minLength={20}
                                rows={6}
                                value={newsForm.message}
                                onChange={(e) => setNewsForm({ ...newsForm, message: e.target.value })}
                                placeholder="Escreva a edição desta semana..."
                                className="w-full bg-ink border border-white/10 focus:border-gold/60 outline-none px-4 py-3 text-sm placeholder:text-slate-600 transition-colors resize-y"
                            />
                        </div>
                        <button
                            data-testid="newsletter-send-button"
                            type="submit"
                            disabled={sending || subscribers.length === 0}
                            className="self-start flex items-center gap-2 bg-gold text-ink font-medium px-6 py-3 text-sm hover:bg-golddark transition-colors disabled:opacity-50"
                        >
                            {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                            Enviar para {subscribers.length} inscrito(s)
                        </button>
                    </form>
                </div>
            </div>

            <AdminCategories />

            <AdminComments />

            <AdminMonetization />

            <AdminCampaigns />

            <div className="mt-14">
                <h2 className="font-serif text-2xl tracking-tight mb-6 flex items-center gap-3">
                    <Mail size={18} className="text-gold" /> Mensagens de contato
                    <span className="text-xs font-sans text-slate-500">({messages.length})</span>
                </h2>
                {messages.length === 0 ? (
                    <p className="text-sm text-slate-500 border border-dashed border-white/15 px-5 py-8 text-center">Nenhuma mensagem recebida ainda.</p>
                ) : (
                    <div className="flex flex-col gap-3">
                        {messages.map((m) => (
                            <div key={m.id} data-testid={`admin-message-${m.id}`} className="border border-white/10 bg-paper p-5">
                                <div className="flex flex-wrap items-baseline gap-3 mb-2">
                                    <span className="font-medium text-sm">{m.name}</span>
                                    <span className="text-xs text-gold">{m.email}</span>
                                    <span className="text-[11px] text-slate-600 ml-auto">{new Date(m.created_at).toLocaleString("pt-BR")}</span>
                                </div>
                                <p className="text-sm text-slate-400 leading-relaxed">{m.message}</p>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </main>
    );
}
